import { useCallback, useEffect, useMemo, useState } from 'react'
import AssetManager from './components/AssetManager'
import CmsResourceWorkspace from './components/CmsResourceWorkspace'
import CmsSidebar from './components/CmsSidebar'
import EmailSubscriptionsWorkspace from './components/EmailSubscriptionsWorkspace'
import EventProgramWorkspace from './components/EventProgramWorkspace'
import cmsApi from './cms.api'
import { cmsResourceMap } from './cms.constants'
import { CmsLookupContext, CmsSectionKey, ICmsAsset, ICmsConfig, IEventProgramStatus } from './cms.types'
import { AuthSessionCtrl } from '@/pages/Auth/useAuthSession'
import css from './cms.module.css'

interface IProps {
  auth: AuthSessionCtrl
}

const emptyConfig: ICmsConfig = {
  partners: [],
  faq: [],
  marketingBlockLineElements: [],
  marketingCards: [],
  gallery: [],
  scenePricingConfigurations: [],
  extraProducts: [],
  headliners: [],
  eventProgram: { exists: false, url: null },
}

const CmsAdmin = ({ auth }: IProps) => {
  const [activeSection, setActiveSection] = useState<CmsSectionKey>('partners')
  const [assets, setAssets] = useState<ICmsAsset[]>([])
  const [assetsLoading, setAssetsLoading] = useState(false)
  const [configSnapshot, setConfigSnapshot] = useState<ICmsConfig>(emptyConfig)

  const loadAssets = useCallback(async () => {
    setAssetsLoading(true)
    const response = await cmsApi.listAssets()
    setAssetsLoading(false)

    if (response.status === 'success') {
      setAssets(response.body)
    }
  }, [])

  const loadConfig = useCallback(async () => {
    const response = await cmsApi.getConfig()
    if (response.status === 'success') {
      setConfigSnapshot(response.body)
    }
  }, [])

  const updateEventProgram = useCallback((eventProgram: IEventProgramStatus) => {
    setConfigSnapshot((snapshot) => ({ ...snapshot, eventProgram }))
  }, [])

  useEffect(() => {
    loadAssets()
    loadConfig()
  }, [loadAssets, loadConfig])

  const lookupContext = useMemo<CmsLookupContext>(
    () => ({
      assets,
      faqCategories: configSnapshot.faq,
      galleryCategories: configSnapshot.gallery,
    }),
    [assets, configSnapshot]
  )

  const resource =
    activeSection === 'assets' || activeSection === 'emailSubscriptions' || activeSection === 'eventProgram'
      ? null
      : cmsResourceMap[activeSection]
  const pageTitle =
    activeSection === 'assets'
      ? 'Медиафайлы'
      : activeSection === 'emailSubscriptions'
        ? 'Email подписчики'
        : activeSection === 'eventProgram'
          ? 'Программа мероприятия'
          : resource?.title

  return (
    <div className={css.app}>
      <CmsSidebar
        activeSection={activeSection}
        userLogin={auth.session?.user.login || ''}
        onChange={setActiveSection}
        onLogout={auth.logout}
      />
      <main className={css.main}>
        <div className={css.topbar}>
          <div>
            <p className="text-heading-5">{pageTitle}</p>
          </div>
        </div>

        <div className={css.workspace}>
          <div className={css.summaryGrid}>
            <div className={css.summaryItem}>
              <p className="text-sm text-description">Партнеры</p>
              <p className="text-heading-5">{configSnapshot.partners.length}</p>
            </div>
            <div className={css.summaryItem}>
              <p className="text-sm text-description">FAQ</p>
              <p className="text-heading-5">
                {configSnapshot.faq.reduce((sum, category) => sum + (category.articles?.length || 0), 0)}
              </p>
            </div>
            <div className={css.summaryItem}>
              <p className="text-sm text-description">Галерея</p>
              <p className="text-heading-5">
                {configSnapshot.gallery.reduce((sum, category) => sum + (category.images?.length || 0), 0)}
              </p>
            </div>
            <div className={css.summaryItem}>
              <p className="text-sm text-description">Медиа</p>
              <p className="text-heading-5">{assets.length}</p>
            </div>
            <div className={css.summaryItem}>
              <p className="text-sm text-description">Программа</p>
              <p className="text-heading-5">{configSnapshot.eventProgram.exists ? 'Есть' : 'Нет'}</p>
            </div>
          </div>

          {activeSection === 'assets' ? (
            <div className={css.tablePanel}>
              <AssetManager assets={assets} loading={assetsLoading} onRefresh={loadAssets} />
            </div>
          ) : activeSection === 'emailSubscriptions' ? (
            <EmailSubscriptionsWorkspace />
          ) : activeSection === 'eventProgram' ? (
            <EventProgramWorkspace initialStatus={configSnapshot.eventProgram} onStatusChange={updateEventProgram} />
          ) : (
            resource && (
              <CmsResourceWorkspace
                key={resource.key}
                resource={resource}
                context={lookupContext}
                assetsLoading={assetsLoading}
                onAssetsRefresh={loadAssets}
                onLookupsRefresh={loadConfig}
              />
            )
          )}
        </div>
      </main>
    </div>
  )
}

export default CmsAdmin
